import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Users, AlertTriangle, TrendingDown, Gauge } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const PATTERNS = ["Full Time", "Part Time", "Contractor", "Shift"];
const BLANK_R = { name: "", role: "", department: "", working_pattern: "Full Time", hours_per_week: 37.5 };
const BLANK_A = { resource_id: "", project_id: "", hours_per_week: "", role_on_project: "" };

const FLAG_STYLE = {
  "Over-allocated": "bg-red-50 text-red-700 ring-red-200",
  "Under-utilised": "bg-amber-50 text-amber-800 ring-amber-200",
  Optimal: "bg-emerald-50 text-emerald-700 ring-emerald-200",
};

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="panel flex items-center gap-4 p-5">
    <div className="rounded-md p-2.5" style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, white)" }}>
      <Icon size={20} style={{ color: "var(--brand-primary)" }} />
    </div>
    <div>
      <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  </div>
);

export default function Resources() {
  const [cap, setCap] = useState(null);
  const [projects, setProjects] = useState([]);
  const [lists, setLists] = useState({});
  const [dialog, setDialog] = useState(null);
  const [res, setRes] = useState(BLANK_R);
  const [alloc, setAlloc] = useState(BLANK_A);

  const load = useCallback(() => api.get("/resources/capacity").then(({ data }) => setCap(data)), []);

  useEffect(() => {
    load();
    api.get("/projects").then(({ data }) => setProjects(data));
    api.get("/config/lists").then(({ data }) => setLists(Object.fromEntries(data.map((l) => [l.key, l.values]))));
  }, [load]);

  const saveResource = async () => {
    if (!res.name.trim()) return toast.error("Resource name is required");
    try {
      await api.post("/resources", { ...res, hours_per_week: Number(res.hours_per_week) || 0 });
      toast.success("Resource added");
      setDialog(null);
      setRes(BLANK_R);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveAllocation = async () => {
    if (!alloc.resource_id || !alloc.project_id) return toast.error("Pick a resource and a project");
    if (!alloc.hours_per_week) return toast.error("Hours per week is required");
    try {
      await api.post("/allocations", { ...alloc, hours_per_week: Number(alloc.hours_per_week) });
      toast.success("Allocation added");
      setDialog(null);
      setAlloc(BLANK_A);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeResource = async (r) => {
    if (!window.confirm(`Remove ${r.name} and their allocations?`)) return;
    await api.delete(`/resources/${r.resource_id}`);
    await load();
    toast.success("Resource removed");
  };

  const removeAllocation = async (a) => {
    await api.delete(`/allocations/${a.id}`);
    await load();
    toast.success("Allocation removed");
  };

  const t = cap?.totals;

  return (
    <Shell
      testId="resources-page"
      title="Resource Capacity"
      subtitle="Working patterns, allocation against capacity, and over/under-resourcing flags"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" data-testid="new-allocation-btn" onClick={() => { setAlloc(BLANK_A); setDialog("allocation"); }} className="gap-1.5">
            <Plus size={15} /> Allocate
          </Button>
          <Button data-testid="new-resource-btn" onClick={() => { setRes(BLANK_R); setDialog("resource"); }} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
            <Plus size={15} /> Add Resource
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Users} label="Resources" value={t?.resources ?? "—"} testId="stat-resources" />
        <Stat icon={Gauge} label="Utilisation" value={t ? `${t.utilisation}%` : "—"} testId="stat-utilisation" />
        <Stat icon={AlertTriangle} label="Over-allocated" value={t?.over_allocated ?? "—"} testId="stat-over" />
        <Stat icon={TrendingDown} label="Under-utilised" value={t?.under_utilised ?? "—"} testId="stat-under" />
      </div>

      {t && (
        <p className="mt-3 text-xs text-slate-500">
          {t.allocated_hours} of {t.capacity_hours} hours per week allocated across the workspace.
        </p>
      )}

      <div className="mt-6 space-y-4">
        {!cap ? (
          <Empty text="Loading capacity…" />
        ) : cap.rows.length === 0 ? (
          <Empty testId="resources-empty" text="No resources yet. Add your delivery team to start capacity planning." />
        ) : (
          cap.rows.map((r) => (
            <section key={r.resource_id} data-testid={`resource-card-${r.resource_id}`} className="panel p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-base font-semibold text-slate-900">{r.name}</h3>
                    <span data-testid={`resource-flag-${r.resource_id}`} className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${FLAG_STYLE[r.flag]}`}>
                      {r.flag}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-400">
                    {r.role || "No role"} · {r.department || "No department"} · {r.working_pattern}
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="font-display text-xl font-bold text-slate-900">{r.utilisation}%</div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">
                      {r.allocated_hours} / {r.capacity_hours} hrs
                    </div>
                  </div>
                  <button data-testid={`delete-resource-${r.resource_id}`} onClick={() => removeResource(r)} aria-label="Delete resource" title="Delete resource" className="text-slate-300 transition-colors hover:text-red-600">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, r.utilisation)}%`,
                    backgroundColor: r.flag === "Over-allocated" ? "#c62828" : r.flag === "Under-utilised" ? "#e6a100" : "#1f8a4c",
                  }}
                />
              </div>

              {r.allocations.length > 0 && (
                <div className="mt-4 divide-y divide-slate-100 border-t border-slate-100 pt-2">
                  {r.allocations.map((a) => (
                    <div key={a.id} data-testid={`allocation-row-${a.id}`} className="flex items-center justify-between py-2 text-sm">
                      <div>
                        <span className="font-medium text-slate-800">{a.project_name}</span>
                        <span className="ml-2 text-xs text-slate-400">{a.role_on_project || "No role"}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-slate-600">{a.hours_per_week} hrs/wk</span>
                        <button data-testid={`delete-allocation-${a.id}`} onClick={() => removeAllocation(a)} aria-label="Remove allocation" title="Remove allocation" className="text-slate-400 transition-colors hover:text-red-600">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))
        )}
      </div>

      <Dialog open={dialog === "resource"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="resource-dialog">
          <DialogHeader>
            <DialogTitle>Add Resource</DialogTitle>
            <DialogDescription>Capacity is derived from the working pattern&rsquo;s contracted hours.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><label className="field-label">Name *</label><Input data-testid="resource-name-input" value={res.name} onChange={(e) => setRes({ ...res, name: e.target.value })} /></div>
            <div><label className="field-label">Role</label><Input data-testid="resource-role-input" value={res.role} onChange={(e) => setRes({ ...res, role: e.target.value })} /></div>
            <div><label className="field-label">Department</label><Select testId="resource-department-select" value={res.department} onChange={(v) => setRes({ ...res, department: v })} options={lists.department || []} placeholder="Select" /></div>
            <div><label className="field-label">Working Pattern</label><Select testId="resource-pattern-select" value={res.working_pattern} onChange={(v) => setRes({ ...res, working_pattern: v })} options={PATTERNS} placeholder="Select" /></div>
            <div><label className="field-label">Capacity (hrs/week)</label><Input data-testid="resource-hours-input" type="number" step="0.5" value={res.hours_per_week} onChange={(e) => setRes({ ...res, hours_per_week: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="resource-cancel-btn" onClick={() => setDialog(null)}>Cancel</Button>
            <Button data-testid="resource-save-btn" onClick={saveResource} style={{ backgroundColor: "var(--brand-primary)" }}>Add Resource</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "allocation"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="allocation-dialog">
          <DialogHeader>
            <DialogTitle>Allocate to Project</DialogTitle>
            <DialogDescription>Allocations above a resource&rsquo;s capacity raise an over-allocation flag.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="field-label">Resource *</label>
              <Select testId="allocation-resource-select" value={alloc.resource_id} onChange={(v) => setAlloc({ ...alloc, resource_id: v })} options={(cap?.rows || []).map((r) => ({ value: r.resource_id, label: r.name }))} placeholder="Select resource" />
            </div>
            <div>
              <label className="field-label">Project *</label>
              <Select testId="allocation-project-select" value={alloc.project_id} onChange={(v) => setAlloc({ ...alloc, project_id: v })} options={projects.map((p) => ({ value: p.id, label: p.name }))} placeholder="Select project" />
            </div>
            <div><label className="field-label">Hours per Week *</label><Input data-testid="allocation-hours-input" type="number" step="0.5" value={alloc.hours_per_week} onChange={(e) => setAlloc({ ...alloc, hours_per_week: e.target.value })} /></div>
            <div><label className="field-label">Role on Project</label><Input data-testid="allocation-role-input" value={alloc.role_on_project} onChange={(e) => setAlloc({ ...alloc, role_on_project: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="allocation-cancel-btn" onClick={() => setDialog(null)}>Cancel</Button>
            <Button data-testid="allocation-save-btn" onClick={saveAllocation} style={{ backgroundColor: "var(--brand-primary)" }}>Allocate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
